import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Spinner from "../common/Spinner";
import Modal from "../common/Modal";
import styles from './CakeOrdersTable.module.css';
import { formatDateInSpanish, sortByTimeAndCreatedAt } from "../../helpers/general";
import { formatPhoneForDisplay } from "../../helpers/phone";
import CakeOrder from './CakeOrder';
import { togglePrePaid } from "../../helpers/cakes";
import ImageReadyModal from './ImageReadyModal';

export default function CakeOrdersTable({ cakeOrders, loading }) {
    const navigate = useNavigate();
    const [localCakeOrders, setLocalCakeOrders] = useState(cakeOrders);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [selectedImageUrl, setSelectedImageUrl] = useState(null);
    const [imageReadyOrder, setImageReadyOrder] = useState(null);

    useEffect(() => {
        setLocalCakeOrders(cakeOrders);
    }, [cakeOrders]);

    function handlePrintImage() {
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
            alert('No se pudo abrir la ventana de impresión. Por favor permite las ventanas emergentes e intenta de nuevo.');
            return;
        }
        const doc = printWindow.document;
        doc.title = 'Print';
        const body = doc.body;
        body.style.cssText = 'margin:0;display:flex;justify-content:center;align-items:center;min-height:100vh;';
        const img = doc.createElement('img');
        img.style.cssText = 'max-width:100%;max-height:100vh;';
        img.onload = () => { printWindow.print(); printWindow.close(); };
        img.src = selectedImageUrl;
        body.appendChild(img);
        setSelectedImageUrl(null);
    }

    async function handleSaveImage() {
        try {
            const response = await fetch(selectedImageUrl);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'imagen.png';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            // Fallback: open in new tab if fetch fails (CORS)
            window.open(selectedImageUrl, '_blank');
        }
        setSelectedImageUrl(null);
    }

    function handleRowClick(cake) {
        setSelectedOrder(cake);
    }

    function closeModal() {
        setSelectedOrder(null);
    }

    if (loading) {
        return <Spinner />;
    }

    // Group orders by date
    let ordersByDate = localCakeOrders.reduce((acc, order) => {
        if (!acc[order.date]) {
            acc[order.date] = [];
        }
        acc[order.date].push(order);
        return acc;
    }, {});


    function handleImageReadyClick(cake) {
        setImageReadyOrder(cake);
    }

    return (
        <div className={styles['table-wrapper']}>
            {Object.keys(ordersByDate).map(date => (
                <div key={date} className={styles['table-container']}>
                    <h2>{formatDateInSpanish(date)} - Total de Pedidos: {ordersByDate[date].length}</h2>
                    <table id="cakeOrderTable">
                        <thead>
                            <tr>
                                <th>Hora</th>
                                <th>Nombre</th>
                                <th>Numero</th>
                                <th>Tamaño</th>
                                <th>Imagen</th>
                                <th>Dedicatoria</th>
                                <th>Comentarios</th>
                                <th>Cajer@</th>
                                <th>Pre-Pagado</th>
                                <th>Imagen</th>
                                <th>Topper</th>
                                <th>Pastel</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ordersByDate[date].sort(sortByTimeAndCreatedAt).map(cake => (
                                <tr key={cake.id} onClick={() => handleRowClick(cake)}>
                                    <td>{cake.time}</td>
                                    <td>{cake.firstName} {cake.lastName}</td>
                                    <td>{formatPhoneForDisplay(cake.phoneNumber)}</td>
                                    <td>{cake.cakeSize}</td>
                                    <td className="imageContainer">
                                        {(cake.createdImageUrl || cake.imageUrl) ? (
                                            <img
                                                src={cake.createdImageUrl || cake.imageUrl}
                                                alt={cake.image || 'Imagen'}
                                                className={styles['image-thumbnail']}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedImageUrl(cake.createdImageUrl || cake.imageUrl);
                                                }}
                                            />
                                        ) : (
                                            cake.image || '—'
                                        )}
                                    </td>
                                    <td>{cake.message}</td>
                                    <td>{cake.comments}</td>
                                    <td>{cake.cashier}</td>
                                    <td className={cake.prePaid ? styles.ready : ''} onClick={(e) => { e.stopPropagation(); togglePrePaid(cake.id, cake.prePaid)}}>{cake.prePaid ? 'Si' : 'No'}</td>
                                    <td
                                        className={
                                            !cake.imageNeeded ? styles['no-image-needed'] :
                                            cake.imageReady ? styles.ready : styles.pending
                                        }
                                        onClick={(e) => { e.stopPropagation(); handleImageReadyClick(cake); }}
                                    >
                                        {!cake.imageNeeded ? 'No necesita' :
                                         cake.imageReady ? 'Lista' : 'Pendiente'}
                                    </td>
                                    <td>
                                        <button
                                            className={styles['topper-design-btn']}
                                            onClick={(e) => { e.stopPropagation(); navigate(`/topper/${cake.id}`); }}
                                            title="Diseñar topper"
                                        >
                                            🎨 Diseñar
                                        </button>
                                    </td>
                                    <td className={cake.ready ? styles.ready : styles.pending}>
                                        {cake.ready ? 'Entregado' : 'En proceso'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ))}
            {selectedOrder && (
                <Modal onClose={closeModal}>
                    <CakeOrder cake={selectedOrder}/>
                </Modal>
            )}

            {selectedImageUrl && (
                <div className={styles['image-action-overlay']} onClick={() => setSelectedImageUrl(null)}>
                    <div className={styles['image-action-modal']} onClick={(e) => e.stopPropagation()}>
                        <img src={selectedImageUrl} alt="Preview" className={styles['image-preview']} />
                        <div className={styles['image-action-buttons']}>
                            <button className={`${styles['image-action-btn']} ${styles['print-btn']}`} onClick={handlePrintImage}>
                                Imprimir
                            </button>
                            <button className={`${styles['image-action-btn']} ${styles['save-btn']}`} onClick={handleSaveImage}>
                                Guardar
                            </button>
                        </div>
                        <button className={styles['image-action-close']} onClick={() => setSelectedImageUrl(null)}>
                            &times;
                        </button>
                    </div>
                </div>
            )}

            {imageReadyOrder && (
                <ImageReadyModal
                    order={imageReadyOrder}
                    onClose={() => setImageReadyOrder(null)}
                    onSuccess={() => setImageReadyOrder(null)}
                />
            )}
        </div>
    );
}
